import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../ThemeContext'; // Adjust the path as necessary
import { getUsers, deleteUser, changeUserStatus } from '../../api/api';
import AddUser from '../AddUser';
import { MantineProvider } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../ConfirmationModal';

export const Example = ({ handlePageUpdate ,updatePage, showSaleModal}) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get the theme from context
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [statusChange,setStatusChange]=useState(false)
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'contact_no',
      header: 'Contact No.',
    },
    {
      accessorKey: 'authority',
      header: 'Authority',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        return (
          <div
            onClick={() => handleChangeStatus(row)}
            style={{ cursor: 'pointer' }}
          >
            {row.getValue('status') === 'Active' ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Actions',
      Cell: ({ row }) => (
        <div className="flex gap-2">
          <PencilIcon
            className="w-5 h-5 text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleEditRow(row)}
          />
          <EyeIcon
            className="w-5 h-5 text-green-600 hover:text-green-800 cursor-pointer"
            onClick={() => navigate('/Users/details', { state: { data: row.original } })}
          />
          <TrashIcon
            className="w-5 h-5 text-red-600 hover:text-red-800 cursor-pointer"
            onClick={() => openDeleteModal(row)}
          />
        </div>
      ),
    },
  ], []);

  const handleEditRow = (row) => {
    setEditingRow(row.original);
    setEditing(true);
  };

  const openDeleteModal = (row) => {
    setRowToDelete(row.original);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Call the delete API here
      await deleteUser(rowToDelete._id);
      setData((prevData) => prevData.filter((item) => item._id !== rowToDelete._id));
      setDeleteModalOpen(false);
      setRowToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleChangeStatus = async (row) => {
    setStatusChange(true)
  
    try {
      // Change the user's status via API
      const res =await changeUserStatus(row.original._id);
      handlePageUpdate()
      setStatusChange(false)
    } catch (error) {
      console.error('Error changing user status:', error);
    }
  };

  useEffect(() => {
    
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        setData(response.data.users || []);
        //console.log(response.data)
        handlePageUpdate()
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, [statusChange, showSaleModal, setEditing]);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnActions: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableRowNumbers: true,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      sx: {
        'thead > tr': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
        },
        'thead > tr > th': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
        'tbody > tr > td': {
          backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
        },
      },
    },
  });

  return (
    <MantineProvider
      theme={{
        colorScheme: theme,
        primaryColor: 'blue',
        primaryShade: 5,
      }}
    >
      <MantineReactTable table={table}
      enableColumnFilters={true}
      />

      {editing && (
        <AddUser
          isEditing={editing}
          setEditing={setEditing}
          userDetails={editingRow}
          handlePageUpdate={handlePageUpdate}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-700 p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              Confirm Delete
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Are you sure you want to delete this user?
            </p>
            <div className="flex justify-end gap-4 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </MantineProvider>
  );
};

export default Example;
